import type { ReactNode } from "react";
import type { GridWidgetType } from "../types";
import type { GridWidgetComponentProps } from "../widgetDefinition";
import { getAllWidgetDefs } from "../widgetRegistry";
import { WidgetShell } from "./WidgetShell";

/** A component that renders a single grid widget by id. */
export type GridWidgetComponent = (props: GridWidgetComponentProps) => ReactNode;

/** Registry of widget type -> component. */
export type GridWidgetRegistry = Partial<Record<GridWidgetType, GridWidgetComponent>>;

/** Build the default registry from all registered widget definitions. */
export const defaultWidgetRegistry: GridWidgetRegistry = Object.fromEntries(
  getAllWidgetDefs().map((def) => [def.type, def.gridComponent]),
);

export type WidgetRenderer = (id: string, type: GridWidgetType) => ReactNode;

/**
 * Returns a render function that looks up the widget type in the registry
 * and renders the component wrapped in WidgetShell for loading/error states.
 */
export function createWidgetRenderer(
  registry: GridWidgetRegistry
): WidgetRenderer {
  return (id: string, type: GridWidgetType) => {
    const Component = registry[type];
    if (!Component) return null;
    return (
      <WidgetShell id={id}>
        <Component id={id} />
      </WidgetShell>
    );
  };
}
